import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2, Printer } from 'lucide-react';
import { format } from 'date-fns';
import type { TFunction } from 'i18next';
import type { OrderPrintTicketStatus, OrderPrintingSummary } from '@/lib/orders-client';
import { printStatusOf, type PrintStatusKind } from './print-status';

/** Explicit light/dark pairs, like the board's payment pill — a tint that works in one mode only reads as broken. */
const CHIP_CLASSES: Record<Exclude<PrintStatusKind, 'none'>, string> = {
  pending: 'border-transparent bg-muted text-muted-foreground',
  printed:
    'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300',
  failed: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-300',
  unsent: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-300',
};

/** Why a ticket did not print, in words: a known code is translated, anything else is shown as sent. */
export function printFailureReason(outcome: OrderPrintTicketStatus, t: TFunction<'orders'>): string {
  if (outcome.status === 'expired') return t('print.status.expired');
  if (!outcome.error) return t('print.status.failed');
  return t(`print.errorCodes.${outcome.error}`, { defaultValue: outcome.error });
}

function clock(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : format(date, 'HH:mm');
}

interface PrintStatusChipProps {
  printing: OrderPrintingSummary | null | undefined;
  /** `compact` = one pill (board card); `full` = one line per ticket type (detail panel). */
  variant?: 'compact' | 'full';
  className?: string;
}

/**
 * What the branch's thermal printers did with this order (see `printStatusOf`). Renders nothing
 * when no printer was involved — the browser print is then the only print there is.
 */
export function PrintStatusChip({ printing, variant = 'compact', className = '' }: PrintStatusChipProps) {
  const { t } = useTranslation('orders');
  const status = printStatusOf({ printing });
  if (status.kind === 'none') return null;

  const alarming = status.kind === 'failed' || status.kind === 'unsent';
  const Icon = status.kind === 'pending' ? Loader2 : alarming ? AlertTriangle : Printer;
  const iconClass = status.kind === 'pending' ? 'h-3 w-3 animate-spin' : 'h-3 w-3';

  if (variant === 'compact') {
    const label =
      status.kind === 'pending'
        ? t('print.status.pending')
        : status.kind === 'unsent'
          ? t('print.status.unsent')
          : status.kind === 'failed'
            ? t('print.status.failedOn', { printer: status.failure?.printerName ?? '' })
            : t('print.status.printed');
    const title =
      status.kind === 'failed' && status.failure
        ? `${status.failure.printerName}: ${printFailureReason(status.failure, t)}`
        : status.kind === 'printed'
          ? t('print.status.printedAt', { time: clock(status.printedAt) })
          : label;
    return (
      <span
        className={`inline-flex max-w-full items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${CHIP_CLASSES[status.kind]} ${className}`}
        title={title}
      >
        <Icon className={`${iconClass} shrink-0`} />
        <span className="truncate">{label}</span>
      </span>
    );
  }

  return (
    <div className={`space-y-1.5 ${className}`}>
      {status.kind === 'unsent' && (
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${CHIP_CLASSES.unsent}`}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          {t('print.status.unsent')}
        </span>
      )}
      {status.kind === 'pending' && (
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${CHIP_CLASSES.pending}`}
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t('print.status.pending')}
        </span>
      )}
      {status.tickets.map((ticket) => {
        const outcome = ticket.outcome!;
        const failed = ticket.kind === 'failed';
        return (
          <div key={ticket.ticketType} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm">
            <span className="text-foreground">
              {t('print.status.ticketLine', {
                ticket: t(`print.ticketTypes.${ticket.ticketType}`),
                printer: outcome.printerName,
              })}
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
                CHIP_CLASSES[failed ? 'failed' : 'printed']
              }`}
            >
              {failed ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              {failed ? printFailureReason(outcome, t) : t('print.status.printedAt', { time: clock(outcome.at) })}
            </span>
          </div>
        );
      })}
    </div>
  );
}
